import { Component, OnInit, OnDestroy, Input, Output, EventEmitter } from '@angular/core';
import { FormControl } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { Subject } from 'rxjs';
import { takeUntil, debounceTime, distinctUntilChanged, switchMap, finalize } from 'rxjs/operators';
import { of } from 'rxjs';
import { MessageService } from '../../../../services/message-service/message.service';
import { BetriebskontakteService } from '../betriebskontakte.service';
import { BetriebSuchResult } from '../betriebskontakt';
import { KieneUtils } from '../../../../utils/KieneUtils';
import {
    BetriebskontakteNeuerKontaktDialogComponent,
    NeuerKontaktDialogResult,
} from '../betriebskontakte-neuer-kontakt-dialog.component';

@Component({
    selector: 'kiene-betriebskontakte-erstellen-dialog',
    templateUrl: './betriebskontakte-erstellen-dialog.component.html',
    styleUrls: ['./betriebskontakte-erstellen-dialog.component.scss'],
})
export class BetriebskontakteErstellenDialogComponent implements OnInit, OnDestroy {
    @Input() zeigeSuche: boolean = false;
    @Input() initialVvvo: string = '';
    @Output() kontaktHinzugefuegt = new EventEmitter<void>();
    @Output() schliesseSuche = new EventEmitter<void>();

    suchErgebnisse: BetriebSuchResult[] = [];
    ladeSuche = false;
    ladeErstelleKontakt = false;
    suchControl = new FormControl('');
    private destroy$ = new Subject<void>();

    constructor(
        private betriebskontakteService: BetriebskontakteService,
        private messageService: MessageService,
        private dialog: MatDialog,
    ) {}

    ngOnInit(): void {
        // Wenn eine VVVO-Nummer übergeben wurde, setze sie in das Suchfeld
        if (this.initialVvvo) {
            this.suchControl.setValue(this.initialVvvo);
        }
        this.setupSearch();
    }

    ngOnDestroy(): void {
        this.destroy$.next();
        this.destroy$.complete();
    }

    /**
     * Verhindert die Eingabe von Nicht-Zahlen
     */
    onKeyPress(event: KeyboardEvent): boolean {
        const charCode = event.which ? event.which : event.keyCode;
        // Erlaube nur Zahlen (0-9)
        if (charCode < 48 || charCode > 57) {
            event.preventDefault();
            return false;
        }
        return true;
    }

    /**
     * Verhindert das Einfügen von Nicht-Zahlen
     */
    onPaste(event: ClipboardEvent): void {
        event.preventDefault();
        const clipboardData = event.clipboardData;
        if (clipboardData) {
            const pastedText = clipboardData.getData('text');
            // Entferne alle Nicht-Zahlen und begrenze auf 15 Zeichen
            const numbersOnly = pastedText.replace(/[^0-9]/g, '').substring(0, 15);
            if (numbersOnly) {
                this.suchControl.setValue(numbersOnly);
                // Automatische Suche wenn 15 Zahlen eingefügt wurden
                if (numbersOnly.length === 15) {
                    // Die valueChanges Subscription wird automatisch ausgelöst
                }
            }
        }
    }

    private setupSearch(): void {
        this.suchControl.valueChanges
            .pipe(
                takeUntil(this.destroy$),
                debounceTime(300),
                distinctUntilChanged(),
                switchMap(value => {
                    if (value && value.length === 15) {
                        this.ladeSuche = true;
                        return this.betriebskontakteService.sucheBetriebe(value);
                    } else {
                        this.suchErgebnisse = [];
                        return of({ records: [], count: 0 });
                    }
                }),
            )
            .subscribe({
                next: response => {
                    this.suchErgebnisse = response.records;
                    this.ladeSuche = false;
                },
                error: error => {
                    this.ladeSuche = false;
                },
            });
    }

    fuegeKontaktHinzu(betrieb: BetriebSuchResult): void {
        this.ladeErstelleKontakt = true;
        this.betriebskontakteService
            .fuegeKontaktHinzu(betrieb.kunde_id)
            .pipe(
                takeUntil(this.destroy$),
                finalize(() => {
                    this.ladeErstelleKontakt = false;
                }),
            )
            .subscribe({
                next: () => {
                    this.messageService.infoMessage('Kontakt erfolgreich hinzugefügt');

                    // Aktualisiere die bereits_kontakt Eigenschaft des Betriebs in der Liste
                    const betriebIndex = this.suchErgebnisse.findIndex(b =>
                        KieneUtils.sameId(b.kunde_id, betrieb.kunde_id),
                    );
                    if (betriebIndex >= 0) {
                        this.suchErgebnisse[betriebIndex].bereits_kontakt = 1;
                    }

                    this.kontaktHinzugefuegt.emit();
                    this.suchControl.setValue('');
                    this.suchErgebnisse = [];
                },
                error: error => {
                    this.messageService.errorMessage('Fehler beim Hinzufügen des Kontakts', error);
                },
            });
    }

    oeffneNeuenKontaktDialog(): void {
        const dialogRef = this.dialog.open(BetriebskontakteNeuerKontaktDialogComponent, {
            width: '90vw',
            minWidth: '90vw',
            data: { vvvo: this.suchControl.value },
            disableClose: true,
        });

        dialogRef.afterClosed().subscribe((result: NeuerKontaktDialogResult) => {
            if (result && result.success) {
                this.kontaktHinzugefuegt.emit();
                this.suchControl.setValue('');
                this.suchErgebnisse = [];
            }
        });
    }

    onAbbrechen(): void {
        this.schliesseSuche.emit();
    }
}
